/**
 * Post Comments Component - LeadFlow V2.0 Module 7
 * Displays and manages comments on a post
 */

import React, { useState, useRef, useEffect } from 'react';
import { Avatar } from 'primereact/avatar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { socialFeedService } from '../../services/socialFeedService';

// Helper function to get user initials
const getUserInitials = (userProfile) => {
    if (!userProfile) return 'U';

    const fullName = userProfile.full_name || userProfile.name || '';

    if (fullName) {
        const names = fullName.trim().split(' ');
        if (names.length >= 2) {
            return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
        }
        return names[0].charAt(0).toUpperCase();
    }

    const firstName = userProfile.first_name || '';
    const lastName = userProfile.last_name || '';

    if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
        return firstName.charAt(0).toUpperCase();
    }

    return 'U';
};

// Simple time formatter
const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
        const interval = Math.floor(seconds / secondsInUnit);
        if (interval >= 1) {
            return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
        }
    }
    return 'just now';
};

const PostComments = ({ postId, onCommentAdded, onCommentDeleted }) => {
    const toast = useRef(null);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(true);
    const [commentsError, setCommentsError] = useState(false); // Track error state
    const [submitting, setSubmitting] = useState(false); // Prevent rapid comment submissions

    // Get current user from localStorage safely
    let currentUser = {};
    try {
        currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) {
        console.error('Error parsing user from localStorage:', e);
    }

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Fetch comments when component mounts
    useEffect(() => {
        loadComments();
    }, [postId]);

    const loadComments = async () => {
        setLoadingComments(true);
        setCommentsError(false);
        try {
            const response = await socialFeedService.getComments(postId);
            if (response.success) {
                // Sort comments by created_at descending (latest first)
                const sortedComments = (response.data || []).sort((a, b) => {
                    return new Date(b.created_at) - new Date(a.created_at);
                });
                setComments(sortedComments);
            } else {
                setCommentsError(true);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
            setCommentsError(true);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim() || submitting) {
            return;
        }

        setLoading(true);
        setSubmitting(true);
        try {
            const response = await socialFeedService.addComment(postId, newComment.trim());

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Your comment has been posted',
                    life: 2000
                });

                // Clear input
                setNewComment('');

                // Reload comments to show the new one
                await loadComments();

                // Notify parent
                if (onCommentAdded) {
                    onCommentAdded();
                }
            } else {
                // Check if it's a moderation failure
                const severity = response.moderation_failed ? 'warn' : 'error';
                const summary = response.moderation_failed ? 'Content Not Allowed' : 'Error';

                toast.current?.show({
                    severity: severity,
                    summary: summary,
                    detail: response.message || 'Failed to add comment',
                    life: 5000 // Longer display time for moderation warnings
                });
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to add comment',
                life: 3000
            });
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const response = await socialFeedService.deleteComment(commentId);
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Deleted',
                    detail: 'Comment has been removed',
                    life: 2000
                });

                // Reload comments
                await loadComments();

                // Notify parent about deletion
                if (onCommentDeleted) {
                    onCommentDeleted();
                }
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete comment',
                life: 3000
            });
        }
    };

    return (
        <div className="post-comments" style={{ padding: isMobile ? '0.75rem' : '1rem' }}>
            <Toast ref={toast} />

            {/* Add Comment Form */}
            <div className="mb-4">
                <div className="flex gap-2 md:gap-3 mb-3">
                    {currentUser.profile_photo_url ? (
                        <Avatar
                            image={currentUser.profile_photo_url}
                            size="normal"
                            shape="circle"
                            className="flex-shrink-0"
                            style={{ width: isMobile ? '36px' : '40px', height: isMobile ? '36px' : '40px' }}
                        />
                    ) : (
                        <Avatar
                            label={getUserInitials(currentUser)}
                            size="normal"
                            shape="circle"
                            className="flex-shrink-0"
                            style={{
                                backgroundColor: '#6366F1',
                                color: '#ffffff',
                                width: isMobile ? '36px' : '40px',
                                height: isMobile ? '36px' : '40px',
                                fontSize: isMobile ? '0.875rem' : '1rem'
                            }}
                        />
                    )}
                    <div className="flex-grow-1">
                        {/* Quick Comment Suggestions - LinkedIn Style */}
                        <div className="flex flex-wrap gap-2 mb-3">
                            <Button
                                label="👏 Congratulations!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Congratulations! 👏')}
                            />
                            <Button
                                label="💯 Great work!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Great work! 💯')}
                            />
                            <Button
                                label="🎉 Amazing!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Amazing! 🎉')}
                            />
                            <Button
                                label="👍 Well done!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Well done! 👍')}
                            />
                            <Button
                                label="🔥 Impressive!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Impressive! 🔥')}
                            />
                            <Button
                                label="💡 Insightful!"
                                className="p-button-outlined"
                                style={{
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => setNewComment('Insightful! 💡')}
                            />
                        </div>

                        <InputTextarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Write a comment..."
                            rows={isMobile ? 3 : 3}
                            className="w-full mb-3"
                            style={{
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                minHeight: isMobile ? '70px' : '80px',
                                padding: isMobile ? '0.65rem' : '0.75rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px'
                            }}
                            autoResize
                        />
                        <div className="flex justify-content-end gap-2">
                            <Button
                                label="Cancel"
                                className="p-button-text"
                                style={{
                                    fontSize: isMobile ? '0.875rem' : '0.95rem',
                                    padding: isMobile ? '0.5rem 1rem' : '0.625rem 1.25rem'
                                }}
                                onClick={() => setNewComment('')}
                                disabled={loading || !newComment.trim()}
                            />
                            <Button
                                label="Comment"
                                icon="pi pi-send"
                                style={{
                                    fontSize: isMobile ? '0.875rem' : '0.95rem',
                                    padding: isMobile ? '0.5rem 1rem' : '0.625rem 1.25rem',
                                    fontWeight: '600'
                                }}
                                onClick={handleAddComment}
                                loading={loading}
                                disabled={!newComment.trim()}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="mb-3 md:mb-4" style={{ margin: isMobile ? '1rem 0' : '1.25rem 0' }} />

            {/* Comments List */}
            <div className="comments-list">
                <h6 className="text-900 font-semibold mb-3" style={{ fontSize: isMobile ? '0.95rem' : '1.1rem' }}>
                    {loadingComments ? 'Loading comments...' : `Comments (${comments.length})`}
                </h6>

                {loadingComments ? (
                    // Loading skeletons
                    <div className="flex flex-column gap-3">
                        <div className="flex gap-3">
                            <Skeleton shape="circle" size="3rem" />
                            <div className="flex-1">
                                <Skeleton width="8rem" height="1rem" className="mb-2" />
                                <Skeleton width="100%" height="3rem" />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Skeleton shape="circle" size="3rem" />
                            <div className="flex-1">
                                <Skeleton width="8rem" height="1rem" className="mb-2" />
                                <Skeleton width="100%" height="3rem" />
                            </div>
                        </div>
                    </div>
                ) : commentsError ? (
                    // Error state with retry option
                    <div className="text-center p-4 md:p-5 border-round surface-ground">
                        <i className="pi pi-exclamation-triangle text-orange-500 mb-3 block" style={{ fontSize: isMobile ? '2.5rem' : '3rem' }}></i>
                        <h5 className="text-700 font-semibold mt-0 mb-2" style={{ fontSize: isMobile ? '0.95rem' : '1.1rem' }}>Failed to load comments</h5>
                        <p className="text-500 m-0 mb-3" style={{ fontSize: isMobile ? '0.875rem' : '0.95rem' }}>
                            Something went wrong. Please try again.
                        </p>
                        <Button
                            label="Retry"
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={loadComments}
                        />
                    </div>
                ) : comments.length > 0 ? (
                    // Comments list
                    <div className="flex flex-column gap-3">
                        {comments.map((comment) => (
                            <div
                                key={comment.id}
                                className="flex gap-2 md:gap-3 p-2 md:p-3 border-round surface-ground"
                            >
                                {comment.user_profile?.profile_photo_url_full ? (
                                    <Avatar
                                        image={comment.user_profile.profile_photo_url_full}
                                        size="normal"
                                        shape="circle"
                                        className="flex-shrink-0"
                                        style={{ width: isMobile ? '36px' : '40px', height: isMobile ? '36px' : '40px' }}
                                    />
                                ) : (
                                    <Avatar
                                        label={getUserInitials(comment.user_profile)}
                                        size="normal"
                                        shape="circle"
                                        className="flex-shrink-0"
                                        style={{
                                            backgroundColor: '#6366F1',
                                            color: '#ffffff',
                                            width: isMobile ? '36px' : '40px',
                                            height: isMobile ? '36px' : '40px',
                                            fontSize: isMobile ? '0.875rem' : '1rem'
                                        }}
                                    />
                                )}
                                <div className="flex-1">
                                    <div className="flex justify-content-between align-items-start mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-900 mb-1" style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                                {comment.user_profile?.full_name || 'Anonymous User'}
                                            </div>
                                            <div className="text-500" style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                {formatTimeAgo(comment.created_at)}
                                            </div>
                                        </div>
                                        {comment.can_delete && (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-text p-button-danger ml-2"
                                                style={{
                                                    padding: isMobile ? '0.35rem' : '0.5rem',
                                                    minWidth: 'auto'
                                                }}
                                                onClick={() => handleDeleteComment(comment.id)}
                                                tooltip="Delete comment"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        )}
                                    </div>
                                    <p className="m-0 text-700 line-height-3" style={{ fontSize: isMobile ? '0.875rem' : '0.95rem' }}>
                                        {comment.comment_text}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // No comments state
                    <div className="text-center p-4 md:p-5 border-round surface-ground">
                        <i className="pi pi-comments text-400 mb-3 block" style={{ fontSize: isMobile ? '3rem' : '4rem' }}></i>
                        <h5 className="text-700 font-semibold mt-0 mb-2" style={{ fontSize: isMobile ? '0.95rem' : '1.1rem' }}>No comments yet</h5>
                        <p className="text-500 m-0" style={{ fontSize: isMobile ? '0.875rem' : '0.95rem' }}>
                            Be the first to share your thoughts on this post!
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PostComments;
